import { httpClient } from './httpClient';
import { ApiResponse } from './errors';
import { formatDate, formatDateGSheet } from '@/utils.ts/date';

/**
 * Generic GET request wrapper for legacy eauget-ajax.php endpoint
 * @param path - The API path parameter
 * @param param - Optional parameter to pass to the endpoint
 */
async function genericGet<T>(path: string, param?: string): Promise<ApiResponse<T>> {
  return httpClient.get<T>('eauget-ajax.php', {
    params: { path, param },
  });
}

/**
 * Get booking availability for a specific month
 * @param year - The year to check availability
 * @param month - The month to check availability (1-12)
 * @returns Promise with availability dates
 */
async function getAvailability(year: number, month: number): Promise<ApiResponse<Date[]>> {
  return httpClient.get<Date[]>('eauget-ajax.php', {
    params: { path: 'availability', year, month },
  });
}

/**
 * Get available services for a specific date
 * @param date - The date to get services for
 */
async function getServices(date: Date): Promise<ApiResponse<any[]>> {
  return genericGet<any[]>('services', formatDateGSheet(date));
}

/**
 * Get available food options
 */
async function getFoods(): Promise<ApiResponse<any[]>> {
  return genericGet<any[]>('foods');
}

/**
 * Get menu for a specific date
 * @param date - The date to get the menu for
 */
async function getMenu(date: Date): Promise<ApiResponse<any[]>> {
  return genericGet<any[]>('menu', formatDate(date));
}

/**
 * Get available drinks
 */
async function getDrinks(): Promise<ApiResponse<any[]>> {
  return genericGet<any[]>('drinks');
}

/**
 * Get available options
 */
async function getOptions(): Promise<ApiResponse<any[]>> {
  return genericGet<any[]>('options');
}

/**
 * Get text content/labels
 */
async function getTexts(): Promise<ApiResponse<any[]>> {
  return genericGet<any[]>('texts');
}

/**
 * Initiate Stripe payment process
 * @param date - Booking date
 * @param type - Booking type
 * @param client - Client information
 * @param amount - Payment amount
 * @param bookingData - Complete booking data
 */
async function startPayment(
  date: string,
  type: string,
  client: string,
  amount: number,
  bookingData: any
): Promise<ApiResponse<any>> {
  console.log('Starting payment', { date, type, client, amount, data: bookingData });

  return httpClient.post('eauget-stripe.php', {
    date,
    type,
    client,
    amount,
    data: bookingData,
  });
}

/**
 * Get Stripe payment status
 * @param id - Stripe payment ID
 */
async function getStripePaymentStatus(id: string): Promise<ApiResponse<any>> {
  return httpClient.get('eauget-stripe-status.php', {
    params: { stripe_id: id },
  });
}

/**
 * Fetch food order details by token
 * @param token - Order token
 */
async function fetchFoodOrder(token: string): Promise<ApiResponse<any>> {
  return httpClient.get('eauget-ajax.php', {
    params: { path: 'order', token },
  });
}

/**
 * Save menu choice for booking
 * @param bookingData - Complete booking data with menu choices
 */
async function saveMenuChoice(bookingData: any): Promise<ApiResponse<any>> {
  return httpClient.post('eauget-save.php', bookingData);
}

/**
 * Validate VAT number using VIES service
 * @param vatNumber - VAT number to validate
 */
async function checkVatNumber(vatNumber: string): Promise<ApiResponse<any>> {
  return httpClient.post('eauget-vies.php', { vat: vatNumber });
}

export {
  checkVatNumber,
  getAvailability,
  getDrinks,
  getFoods,
  getServices,
  getOptions,
  getMenu,
  getTexts,
  getStripePaymentStatus,
  fetchFoodOrder,
  saveMenuChoice,
  startPayment,
};
